import { useState, useEffect } from 'react';
import { expertDashboardApi } from '../../api/expertDashboardApis/expert-dashboard.api';
import { toast } from 'sonner';

export const useExpertSettings = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    bio: '',
    experience: 0,
    email: '',
    phone: '',
    dob: '',
    profileImage: '', 
    city: '',
    state: '',
    pincode: '',
    categoryId: '',
    subCategoryIds: [] as string[], 
    hoursPerDay: 0,
    language: [] as string[],
    expert_setting_status: 'approved', // 🟢 Added to state
    expert_setting_remark: '',         // 🟢 Added to state
  });
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSettingsData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const data = await expertDashboardApi.getHomeData();
      
      let formattedDob = '';
      if (data.dob) {
        formattedDob = typeof data.dob === 'string' ? data.dob.split('T')[0] : '';
      }
      
      setFormData({
        fullName: data.fullName || data.full_name || '',
        bio: data.bio || '',
        experience: data.experience ?? 0, 
        email: data.email || '',
        phone: data.phone || '',
        dob: formattedDob,
        profileImage: data.profileImage || data.profile_image || '',
        city: data.city || '',
        state: data.state || '',
        pincode: data.pincode || '',
        categoryId: data.categoryId || data.category_id || '',
        hoursPerDay: data.hoursPerDay || data.hours_per_day || 0,
        language: data.language ? data.language.split(',').map((s: string) => s.trim()) : [],
        subCategoryIds: Array.isArray(data.subCategoryIds) 
          ? data.subCategoryIds 
          : (data.subCategoryId || data.sub_category_id ? String(data.subCategoryId || data.sub_category_id).split(',') : []),
        // 🟢 FIXED: Ensure we read the status from the database on page load
        expert_setting_status: data.expert_setting_status || data.expertSettingStatus || 'approved',
        expert_setting_remark: data.expert_setting_remark || data.expertSettingRemark || ''
      });

    } catch (err: any) {
      if (err.response?.status === 403) {
        toast.error(err.response?.data?.message || 'Your account is suspended.');
        return;
      }
      toast.error('Failed to load profile settings');
    } finally {
      setIsLoading(false);
    }
  };

  const updateSettings = async (e?: React.FormEvent) => {
    if (e) e.preventDefault(); 
    
    try {
      setIsSaving(true);
      setError(null);

      const dataToSend = new FormData();

      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'profileImage') return;

        if (Array.isArray(value)) {
          if (value.length > 0) dataToSend.append(key, value.join(','));
        } else if (value !== null && value !== undefined) {
          dataToSend.append(key, String(value));
        }
      });

      if (selectedFile) {
        dataToSend.append('profileImageFile', selectedFile);
      }

      await expertDashboardApi.updateSettings(dataToSend);
      
      // 🟢 FIXED: Immediately update local state to trigger the "Under Review" UI without a page reload
      setFormData(prev => ({
        ...prev,
        expert_setting_status: 'under_review',
        expert_setting_remark: ''
      }));

      toast.success('Profile settings updated and sent for review!');
      return true; 
      
    } catch (err: any) {
      console.error('Failed to update settings:', err);
      toast.error(err.response?.data?.message || 'Failed to save changes');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const deactivateAccount = async () => {
    try {
      await expertDashboardApi.deactivateAccount();
      toast.success("Account deactivated successfully. Logging out...");
      localStorage.removeItem('expertCode');
      localStorage.removeItem('expert_code');
      localStorage.removeItem('expertToken');
      setTimeout(() => { window.location.href = '/expert-login'; }, 1500);
    } catch (err) {
      toast.error("Failed to deactivate account");
    }
  };

  useEffect(() => {
    fetchSettingsData();
  }, []);

  return {
    formData,
    setFormData,
    isLoading,
    isSaving,
    error,
    updateSettings,
    deactivateAccount,
    setSelectedFile,
    refetch: fetchSettingsData
  };
};